// Global lock to prevent multiple CartProgressBar instances from adding the same product
if (!window.cartProgressBarLock) {
  window.cartProgressBarLock = {
    isAdding: false,
    pendingStages: {},
    removedStages: {} // Track removed stages with timestamp to prevent immediate re-add
  };
}

if (typeof CartProgressBar !== "function") {
  class CartProgressBar extends HTMLElement {
    constructor() {
      super();
      this.selectedStageProducts = {}; // Track selected products per stage: { stage1: variantId, stage2: variantId, stage3: variantId }
      this.cartTotalCache = null; // Cache for cart total
      this.checkStageProductsTimeout = null; // Debounce timeout
      this.lastProductAddTime = {}; // Track when products were added: { stage1: timestamp, stage2: timestamp, stage3: timestamp }
      this.init();
    }

    init() {
      // Load selected products from localStorage
      const stored = localStorage.getItem("cartProgressSelectedProducts");
      if (stored) {
        this.selectedStageProducts = JSON.parse(stored);
      }
      
      this.updateProgress();
      this.initProductSelection();
      this.initProductPopup();
      this.checkStageProducts();
      
      // Listen for cart updates with debounce
      document.addEventListener("cart-updated", () => {
        this.cartTotalCache = null; // Clear cache
        this.updateProgress();
        // Debounce checkStageProducts to avoid too many calls
        if (this.checkStageProductsTimeout) {
          clearTimeout(this.checkStageProductsTimeout);
        }
        this.checkStageProductsTimeout = setTimeout(() => {
          this.checkStageProducts();
        }, 300);
      });
      
      // Also listen for cart refresh
      if (window.refreshCart) {
        const originalRefreshCart = window.refreshCart;
        window.refreshCart = () => {
          originalRefreshCart();
          this.cartTotalCache = null; // Clear cache
          setTimeout(() => {
            this.updateProgress();
            // Debounce checkStageProducts
            if (this.checkStageProductsTimeout) {
              clearTimeout(this.checkStageProductsTimeout);
            }
            this.checkStageProductsTimeout = setTimeout(() => {
              this.checkStageProducts();
            }, 300);
          }, 300);
        };
      }
    }

    initProductSelection() {
      // Add click handlers to product buttons
      this.querySelectorAll(".cart-progress-bar__product-button").forEach((button) => {
        button.addEventListener("click", (e) => {
          e.preventDefault();
          const productItem = button.closest(".cart-progress-bar__product-item");
          const variantId = productItem.dataset.variantId;
          const productId = productItem.dataset.productId;
          const stage = productItem.closest(".cart-progress-bar__products-stage").dataset.stage;
          
          this.addStageProduct(stage, variantId, productId);
        });
      });
    }

    // Get active stages based on checkboxes
    getActiveStages() {
      const stages = [];
      if (this.dataset.stage1Enabled === "true") {
        stages.push({ number: 1, amount: parseInt(this.dataset.stage1) || 0 });
      }
      if (this.dataset.stage2Enabled === "true") {
        stages.push({ number: 2, amount: parseInt(this.dataset.stage2) || 0 });
      }
      if (this.dataset.stage3Enabled === "true") {
        stages.push({ number: 3, amount: parseInt(this.dataset.stage3) || 0 });
      }
      return stages.sort((a, b) => a.amount - b.amount);
    }

    // Position stages dynamically based on active stages
    positionStages() {
      const stages = this.querySelectorAll(".cart-progress-bar__stage");
      const activeStages = this.getActiveStages();
      const totalStages = activeStages.length;

      stages.forEach((stage, index) => {
        if (index >= activeStages.length) {
          stage.style.display = "none";
          return;
        }

        stage.style.display = "";

        // Reset styles
        stage.style.left = "";
        stage.style.right = "";
        stage.style.transform = "";
        stage.classList.remove("cart-progress-bar__stage--last");

        const span = stage.querySelector("span");
        if (span) {
          span.style.left = "";
          span.style.right = "";
          span.style.transform = "";
          span.style.textAlign = "";
          span.style.maxWidth = "";
        }

        if (totalStages === 1) {
          // Single stage - center
          stage.style.cssText = "left: 50% !important; right: auto !important; transform: translateX(-50%) !important; align-items: center !important;";
          if (span) {
            span.style.textAlign = "center";
            span.style.maxWidth = "120px";
          }
          stage.classList.add("cart-progress-bar__stage--last");
        } else if (totalStages === 2) {
          // Two stages - first at 5%, second at 100%
          if (index === 0) {
            stage.style.cssText = "left: 5% !important; right: auto !important; transform: none !important; align-items: flex-start !important;";
            if (span) {
              span.style.textAlign = "left";
              span.style.maxWidth = "120px";
            }
          } else {
            stage.style.cssText = "left: auto !important; right: 0 !important; transform: none !important; align-items: flex-end !important;";
            stage.classList.add("cart-progress-bar__stage--last");
            if (span) {
              span.style.textAlign = "right";
              span.style.maxWidth = "120px";
            }
          }
        } else {
          // Three stages - equal spacing: 0%, 50%, 100%
          if (index === 0) {
            stage.style.cssText = "left: 0% !important; right: auto !important; transform: none !important; align-items: flex-start !important;";
            if (span) {
              span.style.textAlign = "left";
              span.style.maxWidth = "120px";
            }
          } else if (index === 1) {
            stage.style.cssText = "left: 50% !important; right: auto !important; transform: translateX(-50%) !important; align-items: center !important;";
            if (span) {
              span.style.textAlign = "center";
              span.style.maxWidth = "120px";
            }
          } else {
            stage.style.cssText = "left: auto !important; right: 0% !important; transform: none !important; align-items: flex-end !important;";
            stage.classList.add("cart-progress-bar__stage--last");
            if (span) {
              span.style.textAlign = "right";
              span.style.maxWidth = "120px";
            }
          }
        }
      });
    }

    updateProgress() {
      const cartTotal = this.getCartTotal();
      const activeStages = this.getActiveStages();
      
      if (activeStages.length === 0) return;

      // Position stages dynamically
      this.positionStages();

      // Get stage amounts only for active stages
      const stage1 = activeStages.find(s => s.number === 1)?.amount || 0;
      const stage2 = activeStages.find(s => s.number === 2)?.amount || 0;
      const stage3 = activeStages.find(s => s.number === 3)?.amount || 0;

      // Calculate current stage and progress based on active stages
      let currentStage = 0;
      let progressPercentage = 0;
      let nextStageAmount = activeStages[0]?.amount || 0;
      let remainingAmount = 0;

      // Find which stage we're at (check from highest to lowest)
      for (let i = activeStages.length - 1; i >= 0; i--) {
        if (cartTotal >= activeStages[i].amount) {
          currentStage = activeStages[i].number;
          
          if (i === activeStages.length - 1) {
            // Last stage reached
            progressPercentage = 100;
            nextStageAmount = activeStages[i].amount;
            remainingAmount = 0;
          } else {
            // Between stages - calculate progress
            const currentAmount = activeStages[i].amount;
            const nextAmount = activeStages[i + 1].amount;
            const range = nextAmount - currentAmount;
            const progress = cartTotal - currentAmount;

            // Calculate stage positions based on total active stages
            let currentPosition, nextPosition;
            if (activeStages.length === 2) {
              currentPosition = 5; // First stage at 5%
              nextPosition = 100; // Second stage at 100%
            } else {
              if (i === 0) {
                currentPosition = 5; // First stage at 5%
                nextPosition = 50; // Second stage at 50%
              } else {
                currentPosition = 50; // Second stage at 50%
                nextPosition = 100; // Third stage at 100%
              }
            }

            if (range > 0) {
              const progressRatio = progress / range;
              progressPercentage = currentPosition + (progressRatio * (nextPosition - currentPosition));
            } else {
              progressPercentage = currentPosition;
            }

            nextStageAmount = nextAmount;
            remainingAmount = nextAmount - cartTotal;
          }
          break;
        }
      }

      // Before first stage
      if (currentStage === 0 && cartTotal > 0) {
        const firstStageAmount = activeStages[0].amount;
        if (firstStageAmount > 0) {
          const progressToFirst = (cartTotal / firstStageAmount) * 5; // Max 5% before first stage
          progressPercentage = Math.min(5, Math.max(1, progressToFirst));
        }
        nextStageAmount = firstStageAmount;
        remainingAmount = Math.max(0, firstStageAmount - cartTotal);
      }


      // Update progress bar
      const progressBar = this.querySelector(".cart-progress-bar__fill");
      if (progressBar) {
        // Ensure minimum visibility
        const finalPercentage = progressPercentage > 0 ? Math.max(1, progressPercentage) : 0;
        progressBar.style.width = `${finalPercentage}%`;
      }

      // Update stage indicators based on active stages
      this.querySelectorAll(".cart-progress-bar__stage").forEach((stage, index) => {
        if (index >= activeStages.length) return;
        
        const stageData = activeStages[index];
        stage.classList.remove("cart-progress-bar__stage--completed", "cart-progress-bar__stage--next");
        
        // Only mark as completed if we've reached that stage
        if (cartTotal >= stageData.amount) {
          stage.classList.add("cart-progress-bar__stage--completed");
        }
      });

      // Update message
      const messageEl = this.querySelector(".cart-progress-bar__message");
      const maxStage = activeStages[activeStages.length - 1]?.number;
      if (messageEl) {
        if (currentStage < maxStage) {
          const positiveRemaining = Math.max(0, remainingAmount);
          const formattedAmount = this.formatMoney(positiveRemaining);
          messageEl.textContent = `Sonraki hedefe ${formattedAmount}TL kaldı`;
        } else if (currentStage === maxStage) {
          messageEl.textContent = "Tebrikler! Son aşamaya ulaştınız!";
        }
      }

      // Show/hide product selection based on reached stages
      // Only update if we're not in the middle of adding a product (global lock)
      if (!window.cartProgressBarLock.isAdding) {
        this.updateProductSelection(cartTotal, stage1, stage2, stage3);
      }
    }

    updateProductSelection(cartTotal, stage1, stage2, stage3) {
      const productsOverlay = this.querySelector(".cart-progress-bar__products-overlay");
      if (!productsOverlay) return;

      // Prevent re-entry if already adding a product (global lock)
      if (window.cartProgressBarLock.isAdding) return;

      const activeStages = this.getActiveStages();

      // Ensure selectedStageProducts is loaded from localStorage (shared across instances)
      const stored = localStorage.getItem("cartProgressSelectedProducts");
      if (stored) {
        this.selectedStageProducts = JSON.parse(stored);
      } else {
        this.selectedStageProducts = {};
      }

      // Hide all stage products first
      activeStages.forEach(stage => {
        const stageProducts = this.querySelector(`.cart-progress-bar__products-stage[data-stage="${stage.number}"]`);
        if (stageProducts) stageProducts.style.display = "none";
      });
      
      // Hide overlay by default
      productsOverlay.style.display = "none";

      // Show products only for the highest reached stage that doesn't have a product selected yet
      // Check from highest to lowest stage
      const now = Date.now();
      const removeCooldown = 5000; // 5 seconds cooldown after removal
      
      for (let i = activeStages.length - 1; i >= 0; i--) {
        const stage = activeStages[i];
        const stageAmount = stage.number === 1 ? stage1 : stage.number === 2 ? stage2 : stage3;
        const stageKey = `stage${stage.number}`;
        
        // Skip if already selected or pending (check global lock)
        if (this.selectedStageProducts[stageKey] || window.cartProgressBarLock.pendingStages[stageKey]) {
          continue;
        }
        
        // Skip if recently removed (cooldown period)
        const removedTime = window.cartProgressBarLock.removedStages[stageKey];
        if (removedTime && (now - removedTime) < removeCooldown) {
          continue;
        }
        
        if (cartTotal >= stageAmount) {
          const stageProducts = this.querySelector(`.cart-progress-bar__products-stage[data-stage="${stage.number}"]`);
          if (!stageProducts) continue;

          const stageItems = stageProducts.querySelectorAll(".cart-progress-bar__product-item");
          if (stageItems.length === 1) {
            // Mark as pending immediately using global lock
            window.cartProgressBarLock.pendingStages[stageKey] = true;
            
            // Auto-add if only 1 product available
            const productItem = stageItems[0];
            const variantId = productItem.dataset.variantId;
            const productId = productItem.dataset.productId;
            if (variantId && productId) {
              this.addStageProduct(stage.number, parseInt(variantId), parseInt(productId));
            }
            return; // Exit immediately after starting add
          } else if (stageItems.length > 1) {
            stageProducts.style.display = "flex";
            productsOverlay.style.display = "flex";
          }
          break; // Only show the highest reached stage
        }
      }
    }

    initProductPopup() {
      const productsOverlay = this.querySelector(".cart-progress-bar__products-overlay");
      if (!productsOverlay) return;

      // Close button
      const closeButton = productsOverlay.querySelector(".cart-progress-bar__products-close");
      if (closeButton) {
        closeButton.addEventListener("click", () => {
          this.closeProductPopup();
        });
      }

      // Close on overlay click
      productsOverlay.addEventListener("click", (e) => {
        if (e.target === productsOverlay) {
          this.closeProductPopup();
        }
      });

      // Close on ESC key
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && productsOverlay.style.display === "flex") {
          this.closeProductPopup();
        }
      });
    }

    closeProductPopup() {
      const productsOverlay = this.querySelector(".cart-progress-bar__products-overlay");
      if (productsOverlay) {
        productsOverlay.style.display = "none";
      }
    }

    async addStageProduct(stage, variantId, productId) {
      const stageKey = `stage${stage}`;
      
      // Check global lock - if already adding, exit
      if (window.cartProgressBarLock.isAdding) {
        return;
      }
      
      try {
        // Set global lock to prevent all instances from adding
        window.cartProgressBarLock.isAdding = true;
        window.cartProgressBarLock.pendingStages[stageKey] = true;
        
        // Mark as selected immediately to prevent race conditions
        this.selectedStageProducts[stageKey] = variantId;
        localStorage.setItem("cartProgressSelectedProducts", JSON.stringify(this.selectedStageProducts));
        
        // Add product to cart with stage gift property
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1,
              properties: {
                "_stage_gift": "true"
              }
            }]
          })
        });

        if (response.ok) {
          // Track when product was added
          this.lastProductAddTime[stageKey] = Date.now();
          
          // Hide product selection popup immediately after adding product
          this.closeProductPopup();
          
          // Update cart - but don't check products immediately
          if (window.refreshCart) {
            window.refreshCart();
          }
          
          // Update progress after a delay to ensure cart is updated
          this.cartTotalCache = null; // Clear cache
          setTimeout(() => {
            // Release global lock
            window.cartProgressBarLock.isAdding = false;
            delete window.cartProgressBarLock.pendingStages[stageKey];
            this.updateProgress();
          }, 500);
        } else {
          // Revert on failure
          delete this.selectedStageProducts[stageKey];
          localStorage.setItem("cartProgressSelectedProducts", JSON.stringify(this.selectedStageProducts));
          // Release global lock
          window.cartProgressBarLock.isAdding = false;
          delete window.cartProgressBarLock.pendingStages[stageKey];
        }
      } catch (error) {
        console.error("Error adding product to cart:", error);
        // Revert on error
        delete this.selectedStageProducts[stageKey];
        localStorage.setItem("cartProgressSelectedProducts", JSON.stringify(this.selectedStageProducts));
        // Release global lock
        window.cartProgressBarLock.isAdding = false;
        delete window.cartProgressBarLock.pendingStages[stageKey];
      }
    }

    async checkStageProducts() {
      // Get cart total from API to ensure accuracy (especially after adding products)
      let cartTotal = 0;
      try {
        const cartResponse = await fetch("/cart.js");
        const cart = await cartResponse.json();
        cartTotal = cart.total_price;
        this.cartTotalCache = cartTotal; // Update cache
      } catch (error) {
        // Fallback to DOM method
        cartTotal = this.getCartTotal();
      }
      
      const activeStages = this.getActiveStages();

      // Check if we need to remove products - only check if we have selected products
      const hasSelectedProducts = activeStages.some(stage => 
        this.selectedStageProducts[`stage${stage.number}`]
      );
      
      if (!hasSelectedProducts) {
        return; // Early return if no products to check
      }

      // Don't remove products that were just added (within last 3 seconds)
      const now = Date.now();
      const minTimeSinceAdd = 3000; // 3 seconds

      // Only remove product if cart total is significantly below the stage threshold
      const buffer = 500; // 5 TL buffer (in cents)
      
      // Batch removals to avoid multiple API calls
      const removals = [];
      
      activeStages.forEach(stage => {
        const stageAmount = stage.number === 1 ? parseInt(this.dataset.stage1) || 0 :
                           stage.number === 2 ? parseInt(this.dataset.stage2) || 0 :
                           parseInt(this.dataset.stage3) || 0;
        
        if (cartTotal < (stageAmount - buffer) && this.selectedStageProducts[`stage${stage.number}`]) {
          const timeSinceAdd = this.lastProductAddTime[`stage${stage.number}`] ? 
            (now - this.lastProductAddTime[`stage${stage.number}`]) : Infinity;
          if (timeSinceAdd > minTimeSinceAdd) {
            removals.push({ stage: stage.number, variantId: this.selectedStageProducts[`stage${stage.number}`] });
          }
        }
      });

      // Execute removals
      for (const removal of removals) {
        const stageKey = `stage${removal.stage}`;
        // Mark as removed to prevent immediate re-add (5 second cooldown)
        window.cartProgressBarLock.removedStages[stageKey] = Date.now();
        
        await this.removeStageProduct(removal.stage, removal.variantId);
        delete this.selectedStageProducts[stageKey];
        delete this.lastProductAddTime[stageKey];
      }

      // Update localStorage only if we made changes
      if (removals.length > 0) {
        localStorage.setItem("cartProgressSelectedProducts", JSON.stringify(this.selectedStageProducts));
        this.cartTotalCache = null; // Clear cache after removal
      }
    }

    async removeStageProduct(stage, variantId) {
      try {
        // Get current cart to find the item
        const cartResponse = await fetch("/cart.js");
        const cart = await cartResponse.json();
        
        // Find item by variant ID AND _stage_gift property
        const cartItem = cart.items.find(item => {
          if (item.variant_id !== parseInt(variantId)) return false;
          // Check if it has _stage_gift property
          if (item.properties && item.properties._stage_gift === "true") {
            return true;
          }
          return false;
        });
        
        if (cartItem) {
          // Remove from cart using the item key directly
          await fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              id: cartItem.key,
              quantity: 0
            })
          });

          // Refresh cart
          if (window.refreshCart) {
            window.refreshCart();
          }
        }
      } catch (error) {
        console.error("Error removing product from cart:", error);
      }
    }

    getCartTotal() {
      // Use cache if available
      if (this.cartTotalCache !== null) {
        return this.cartTotalCache;
      }

      // Try to get from data attribute first (fastest)
      if (this.dataset.cartTotal) {
        this.cartTotalCache = parseInt(this.dataset.cartTotal);
        return this.cartTotalCache;
      }

      // Try to get from cart total element
      const cartTotalEl = document.querySelector("[data-cart-total]");
      if (cartTotalEl) {
        const totalText = cartTotalEl.textContent.trim();
        this.cartTotalCache = this.parseMoney(totalText);
        return this.cartTotalCache;
      }

      // Fallback: try to calculate from cart items
      let total = 0;
      document.querySelectorAll("[data-js-cart-item]").forEach((item) => {
        const priceEl = item.querySelector(".cart-item__price strong, .cart-item__total strong");
        if (priceEl) {
          const price = this.parseMoney(priceEl.textContent);
          const qtyEl = item.querySelector(".qty");
          const qty = qtyEl ? parseInt(qtyEl.value) || 1 : 1;
          total += price * qty;
        }
      });

      this.cartTotalCache = total;
      return total;
    }

    parseMoney(moneyString) {
      // Remove currency symbols and parse number
      // Handle different formats: "₺100.50", "100,50 ₺", "$100.50", etc.
      const cleaned = moneyString.replace(/[^\d.,]/g, "");
      // Handle comma as decimal separator (European format)
      let numericValue;
      if (cleaned.includes(",") && cleaned.includes(".")) {
        // Both comma and dot: assume dot is thousands separator
        numericValue = cleaned.replace(/\./g, "").replace(",", ".");
      } else if (cleaned.includes(",")) {
        // Only comma: could be decimal or thousands separator
        // If more than 2 digits after comma, assume thousands separator
        const parts = cleaned.split(",");
        if (parts[1] && parts[1].length > 2) {
          numericValue = cleaned.replace(/,/g, "");
        } else {
          numericValue = cleaned.replace(",", ".");
        }
      } else {
        numericValue = cleaned;
      }
      return Math.round(parseFloat(numericValue) * 100) || 0;
    }

    formatMoney(amount) {
      // Format as currency (cents to currency)
      // Ensure amount is a number and positive
      const numAmount = Math.max(0, Number(amount) || 0);
      // Format with 2 decimal places, use comma as decimal separator
      const formatted = (numAmount / 100).toFixed(2).replace(".", ",");
      // Remove trailing zeros if it's a whole number (100,00 -> 100)
      const cleanFormatted = formatted.replace(/,00$/, "");
      // Return formatted amount (always return a string, never empty)
      return cleanFormatted || "0";
    }

    getStageText(stageNumber) {
      const stageTexts = [
        this.dataset.stage1Text || "Stage 1",
        this.dataset.stage2Text || "Stage 2",
        this.dataset.stage3Text || "Stage 3"
      ];
      return stageTexts[stageNumber - 1] || "";
    }
  }

  if (typeof customElements.get("cart-progress-bar") == "undefined") {
    customElements.define("cart-progress-bar", CartProgressBar);
  }
}

